import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, BookOpen, ChevronRight, PlayCircle, AlertCircle, CheckCircle2 } from "lucide-react";
import { useGetMyLogbooksQuery, useStartLogbookMutation, useRectifyLogbookStageMutation } from "../api/logbookApi";
import { useMeQuery } from "../../auth/api/authApi";
import { describeApiError } from "../../../lib/apiError";
import PayAssessmentButton, {
  AssessmentPaymentClosedNotice,
  isAssessmentPaymentOpen,
} from "../components/PayAssessmentButton";

function safeDate(dateLike?: string | null) {
  if (!dateLike) return "—";
  return String(dateLike).slice(0, 10);
}

function fullNameFromSupervisor(sup: any) {
  if (!sup) return "—";
  const parts = [sup.title, sup.firstname, sup.othernames, sup.surname].filter(Boolean);
  return parts.join(" ");
}

function normalizeRole(input?: any): string {
  // roles[0] may be a Spatie object { name: "probationer" } or a plain string
  const raw = typeof input === "object" && input !== null ? (input.name ?? "") : (input ?? "");
  const r = String(raw).toLowerCase().trim().replace(/\s+/g, "_");
  if (["admin", "administrator", "superadmin", "super_admin"].includes(r)) return "admin";
  if (["probationer"].includes(r)) return "probationer";
  if (["student"].includes(r)) return "student";
  if (["technician"].includes(r)) return "technician";
  if (["graduate"].includes(r)) return "graduate";
  if (["member", "corporate", "user"].includes(r)) return "member";
  if (["fellow"].includes(r)) return "fellow";
  return r;
}

// Whether the member may view this logbook's result.
//  - Legacy logbooks (no assessment diet): viewable when testing_grade is SUCCESSFUL (unchanged).
//  - New assessment-diet logbooks: viewable only after the board approves the result.
function canViewResult(lb: any): boolean {
  if (lb?.logbook_assessment_diet_id) return !!lb?.result_approved_at;
  return lb?.testing_grade === "SUCCESSFUL";
}

function StatusBadge({ status }: { status?: string }) {
  const s = String(status || "").toLowerCase();
  const map: Record<string, string> = {
    approved:           "bg-green-100 text-green-700 border-green-200",
    assessment_ongoing: "bg-green-100 text-green-700 border-green-200",
    submitted:          "bg-yellow-100 text-yellow-700 border-yellow-200",
    pending:            "bg-yellow-100 text-yellow-700 border-yellow-200",
    rejected:           "bg-red-100 text-red-700 border-red-200",
    in_progress:        "bg-blue-100 text-blue-700 border-blue-200",
    graded:             "bg-indigo-100 text-indigo-700 border-indigo-200",
    repeating:          "bg-orange-100 text-orange-700 border-orange-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border uppercase tracking-wide ${map[s] ?? "bg-gray-100 text-gray-600 border-gray-200"}`}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

/**
 * Did this logbook pass?
 *
 * The server is the authority — it appends `is_passed`, computed by
 * Logbook::hasPassed(), and that is the same rule the endpoint behind the
 * button uses to decide which stage to create. Reading it here is what keeps
 * the label and the outcome in step.
 *
 * The local derivation is only a fallback for an API that predates the field.
 * It has to know both gradings: the legacy import wrote 'SUCCESSFUL' into
 * `testing_grade`, while every assessment since writes 'pass' into
 * `final_grade`. Testing only the legacy column is what made this page count a
 * freshly-passed stage as no pass at all, so Start Logbook offered — and the
 * server created — the stage the candidate had just passed.
 */
const PASS_GRADES = ["pass", "passed", "successful"];

function isPassed(lb: any): boolean {
  if (typeof lb?.is_passed === "boolean") return lb.is_passed;

  if (!["graded", "completed"].includes(String(lb?.status).toLowerCase())) return false;

  // A diet-assessed result is not settled until the board releases it.
  if (lb?.logbook_assessment_diet_id && !lb?.result_approved_at) return false;

  return [lb?.final_grade, lb?.testing_grade].some((g) =>
    PASS_GRADES.includes(String(g ?? "").trim().toLowerCase()),
  );
}

const ELIGIBLE_ROLES = ["probationer", "student", "technician"];
/** Statuses a mis-staged logbook can still be renumbered in — mirrors the API. */
const RECTIFIABLE_STATUSES = ["in_progress", "submitted", "awaiting_assessment", "repeating"];
const ONGOING_STATUSES = ["in_progress", "submitted", "awaiting_assessment", "repeating", "accepted", "completed"];

export default function MyLogbooks() {
  const navigate = useNavigate();
  const { data: me, isLoading: meLoading } = useMeQuery();
  const { data: logbooks = [], isLoading, error, refetch, isFetching } =
    useGetMyLogbooksQuery();
  const [startLogbook, { isLoading: starting }] = useStartLogbookMutation();
  const [rectifyStage, { isLoading: rectifying }] = useRectifyLogbookStageMutation();
  const [banner, setBanner] = useState<{ ok: boolean; msg: string } | null>(null);

  const userRole = useMemo(() => {
    const raw = (me as any)?.user ?? me;
    return normalizeRole(raw?.roles?.[0] ?? raw?.whois);
  }, [me]);

  // Compute start-stage eligibility
  const startInfo = useMemo(() => {
    // Wait for the page to finish loading. Until the list arrives `logbooks` is
    // an empty array, which looks exactly like "nothing started yet" — so on a
    // slow connection the button offered Stage 1 to a candidate who already had
    // a logbook open, and it stayed offered even when the request failed
    // outright. No list, no offer.
    if (isLoading || meLoading || error) return null;

    if (!ELIGIBLE_ROLES.includes(userRole)) return null;

    const passedCount = logbooks.filter(isPassed).length;

    if (passedCount >= 3) return null; // All 3 stages complete

    // Hide if ANY logbook is still active/in-flight
    const hasOngoing = logbooks.some((lb: any) =>
      ONGOING_STATUSES.includes(String(lb.status).toLowerCase()),
    );

    // Also hide while a diet-assessed logbook is graded but not yet board-approved
    // — the outcome (and whether a new stage is due) isn't settled until then.
    const hasAwaitingApproval = logbooks.some(
      (lb: any) =>
        String(lb.status).toLowerCase() === "graded" &&
        !!lb.logbook_assessment_diet_id &&
        !lb.result_approved_at,
    );

    if (hasOngoing || hasAwaitingApproval) return null;

    const nextStage = passedCount + 1;
    return { nextStage };
  }, [logbooks, userRole, isLoading, meLoading, error]);

  /**
   * A logbook opened at a stage this candidate has already passed.
   *
   * Start Logbook used to count only the legacy grade column, so a candidate
   * who passed Stage 1 under the current assessment flow was handed Stage 1
   * again. The count is fixed, but the wrongly-numbered logbooks it already
   * created are still sitting on these pages — this is what offers to renumber
   * one.
   *
   * A REPEAT is not this: failing Stage 1 is supposed to produce a second
   * Stage 1. Only a stage the candidate has actually PASSED is wrong.
   */
  const misStaged = useMemo(() => {
    const passedCount = logbooks.filter(isPassed).length;
    if (passedCount === 0 || passedCount >= 3) return null;

    const open = logbooks.filter((lb: any) =>
      RECTIFIABLE_STATUSES.includes(String(lb.status).toLowerCase()),
    );

    // More than one open logbook is ambiguous — the server refuses those too.
    if (open.length !== 1) return null;
    if (Number(open[0].stage) > passedCount) return null;

    return { current: Number(open[0].stage), correct: passedCount + 1 };
  }, [logbooks]);

  async function handleRectify() {
    try {
      const res = await rectifyStage().unwrap();
      flash(true, res.message ?? "Logbook stage corrected.");
      refetch();
    } catch (e: any) {
      flash(false, e?.data?.message || "Could not correct the logbook stage.");
    }
  }

  function flash(ok: boolean, msg: string) {
    setBanner({ ok, msg });
    setTimeout(() => setBanner(null), 5000);
  }

  async function handleStart() {
    try {
      const res = await startLogbook().unwrap();
      flash(true, res.message ?? "Logbook started successfully.");
      refetch();
    } catch (e: any) {
      const msg =
        e?.data?.message ||
        e?.error?.data?.message ||
        "Failed to start logbook.";
      flash(false, msg);
    }
  }

  return (
    <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">

      {/* Toast */}
      {banner && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-bold text-white ${banner.ok ? "bg-green-600" : "bg-red-600"}`}>
          {banner.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {banner.msg}
        </div>
      )}

      {/* A logbook opened at a stage this candidate has already passed. Shown
          above the list rather than beside the Start button, because Start is
          hidden while a logbook is open — which is exactly the state a
          mis-staged one leaves them in, with nothing on the page to explain
          why the number is wrong or any way to put it right. */}
      {misStaged && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-amber-900">
                This logbook was opened at the wrong stage
              </div>
              <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                You have already passed Stage {misStaged.current}, but your open logbook was
                created as Stage {misStaged.current} again. It should be Stage {misStaged.correct}.
                Correcting it keeps everything you have entered — only the stage number changes.
              </p>
              <button
                onClick={handleRectify}
                disabled={rectifying}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg disabled:opacity-40 transition-colors"
              >
                {rectifying
                  ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  : <CheckCircle2 className="w-3.5 h-3.5" />}
                Rectify to Stage {misStaged.correct}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border shadow-sm">

        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b gap-4 flex-wrap">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900">My Logbooks</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Select a logbook to view details and weekly entries.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Start Stage button — only shown when eligible */}
            {startInfo && (
              <button
                onClick={handleStart}
                disabled={starting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg disabled:opacity-40 transition-colors"
              >
                {starting
                  ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  : <PlayCircle className="w-3.5 h-3.5" />}
                Start Stage {startInfo.nextStage}
              </button>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-1.5 text-xs border px-3 py-1.5 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* States */}
        {isLoading && (
          <div className="py-12 text-center text-sm text-gray-500">Loading…</div>
        )}

        {error && (
          <div className="m-4 sm:m-6 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-4">
            <p className="font-semibold">Failed to load logbooks.</p>
            <p className="mt-1 text-xs leading-relaxed">{describeApiError(error)}</p>
          </div>
        )}

        {!isLoading && !error && logbooks.length === 0 && (
          <div className="py-14 text-center">
            <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No logbook found.</p>
            {startInfo && (
              <p className="text-xs text-indigo-600 mt-2">
                Use the "Start Stage {startInfo.nextStage}" button above to begin.
              </p>
            )}
          </div>
        )}

        {/* ── Mobile: card list ───────────────────────────────────────────────── */}
        {logbooks.length > 0 && (
          <>
            <div className="divide-y sm:hidden">
              {logbooks.map((lb: any) => (
                <div key={lb.id} className="p-4 space-y-3">
                  {/* diet + status row */}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">
                        {lb?.diet?.title || "—"}
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        {safeDate(lb?.diet?.start_date)} → {safeDate(lb?.diet?.end_date)}
                      </div>
                    </div>
                    <StatusBadge status={lb.status} />
                  </div>

                  {/* meta row */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                    <div>
                      <span className="text-gray-400 uppercase tracking-wider text-[10px] font-semibold block">Level</span>
                      <span className="text-gray-700 font-medium">Stage {lb.stage ?? "—"}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 uppercase tracking-wider text-[10px] font-semibold block">Progress</span>
                      <span className="text-gray-700 font-medium">{Number(lb.position || 0)} / {Number(lb.size || 0)}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400 uppercase tracking-wider text-[10px] font-semibold block">Supervisor</span>
                      <span className="text-gray-700 font-medium">{fullNameFromSupervisor(lb.supervisor)}</span>
                    </div>
                  </div>

                  {/* actions */}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => navigate(`/logbook/${lb.id}`)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border rounded-lg text-xs font-semibold hover:bg-gray-50 transition-colors"
                    >
                      View Logbook <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    {String(lb.status).toLowerCase() === "submitted" && !lb.assessment_paid && (
                      <PayAssessmentButton
                        logbookId={lb.id}
                        className="flex-1"
                        label="Pay Assessment Fee"
                        paymentOpen={isAssessmentPaymentOpen(lb)}
                      />
                    )}
                    {canViewResult(lb) && (
                      <button
                        onClick={() => navigate(`/logbook/${lb.id}/result`)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-green-300 rounded-lg text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
                      >
                        View Result
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Desktop: table ───────────────────────────────────────────────── */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    <th className="px-4 py-3">Diet</th>
                    <th className="px-4 py-3">Stage</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Supervisor</th>
                    <th className="px-4 py-3">Progress</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {logbooks.map((lb: any) => (
                    <tr key={lb.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{lb?.diet?.title || "—"}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {safeDate(lb?.diet?.start_date)} → {safeDate(lb?.diet?.end_date)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">Stage {lb.stage ?? "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={lb.status} /></td>
                      <td className="px-4 py-3 text-gray-700 text-xs">{fullNameFromSupervisor(lb.supervisor)}</td>
                      <td className="px-4 py-3 text-gray-700 text-xs">
                        {Number(lb.position || 0)} / {Number(lb.size || 0)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/logbook/${lb.id}`)}
                            className="px-3 py-1.5 border rounded-lg text-xs font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap"
                          >
                            View
                          </button>
                          {String(lb.status).toLowerCase() === "submitted" && !lb.assessment_paid && (
                            isAssessmentPaymentOpen(lb) ? (
                              <PayAssessmentButton
                                logbookId={lb.id}
                                label="Pay Assessment"
                                className="whitespace-nowrap"
                                paymentOpen
                              />
                            ) : (
                              <AssessmentPaymentClosedNotice className="max-w-[210px]" />
                            )
                          )}
                          {canViewResult(lb) && (
                            <button
                              onClick={() => navigate(`/logbook/${lb.id}/result`)}
                              className="px-3 py-1.5 border border-green-300 rounded-lg text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 transition-colors whitespace-nowrap"
                            >
                              View Result
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
